import { useState } from 'react';
import { Link } from 'react-router-dom';
import { waitlistApi } from '../api/client';
import {
  CheckIcon,
  ArrowRightIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  BoltIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';

export default function Landing() {
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [storeCount, setStoreCount] = useState('');
  const [aggregators, setAggregators] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const toggleAggregator = (agg: string) => {
    setAggregators((prev) =>
      prev.includes(agg) ? prev.filter((a) => a !== agg) : [...prev, agg]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await waitlistApi.join({
        email,
        company_name: companyName,
        store_count: storeCount ? parseInt(storeCount) : undefined,
        aggregators: aggregators.length > 0 ? aggregators : undefined,
      });
      setSubmitted(true);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to join waitlist';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="container mx-auto px-4 sm:px-6 py-4 sm:py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg shadow-blue-500/30">
              <span className="text-lg sm:text-xl font-bold text-white">R</span>
            </div>
            <span className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight">RecoverRefunds</span>
          </div>
          <nav className="flex items-center gap-3 sm:gap-6">
            <a href="#features" className="hidden sm:block text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors">
              Features
            </a>
            <a href="#pricing" className="hidden sm:block text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors">
              Pricing
            </a>
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg sm:rounded-xl text-sm font-medium hover:from-blue-700 hover:to-blue-600 shadow-lg shadow-blue-500/25 transition-all"
            >
              Sign In
              <ArrowRightIcon className="w-4 h-4" />
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="container mx-auto px-4 sm:px-6 py-12 sm:py-20">
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
          {/* Left Content */}
          <div className="flex-1 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-medium mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
              </span>
              Beta Now Available
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-slate-900 leading-tight mb-4 sm:mb-6">
              Stop Losing Money to{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                Fraudulent Refunds
              </span>
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-slate-600 mb-6 sm:mb-8 max-w-xl">
              Restaurants lose billions annually to fake missing item claims on delivery platforms.
              RecoverRefunds helps you fight back with AI-powered dispute management.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
              <StatCard value="50%" label="Win Rate" />
              <StatCard value="$700K+" label="Recovered/Year" />
              <StatCard value="500+" label="Stores" />
              <StatCard value="5 min" label="Setup" />
            </div>
          </div>

          {/* Right - Waitlist Form */}
          <div className="w-full max-w-md">
            <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-200 p-8">
              {submitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                    <CheckIcon className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">You're on the list!</h3>
                  <p className="text-slate-600">We'll email you when your spot opens.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-semibold text-slate-900">Join the Beta Waitlist</h3>
                    <p className="text-sm text-slate-500 mt-1">Get early access and special pricing</p>
                  </div>

                  {error && (
                    <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl text-sm border border-red-200">
                      {error}
                    </div>
                  )}

                  <div>
                    <input
                      type="email"
                      placeholder="Work email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                    <input
                      type="text"
                      placeholder="Restaurant/Franchise name"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                    <select
                      value={storeCount}
                      onChange={(e) => setStoreCount(e.target.value)}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    >
                      <option value="">Number of stores</option>
                      <option value="5">1-5</option>
                      <option value="20">6-20</option>
                      <option value="50">21-50</option>
                      <option value="100">50+</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Platforms used:</label>
                    <div className="flex flex-wrap gap-2">
                      {['Uber Eats', 'DoorDash', 'Grubhub'].map((agg) => (
                        <button
                          key={agg}
                          type="button"
                          onClick={() => toggleAggregator(agg.toLowerCase().replace(' ', ''))}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                            aggregators.includes(agg.toLowerCase().replace(' ', ''))
                              ? 'bg-blue-50 border-blue-300 text-blue-700'
                              : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                          }`}
                        >
                          {agg}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-medium rounded-xl hover:from-blue-700 hover:to-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-500/30"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Joining...
                      </span>
                    ) : (
                      'Join Waitlist - Free Beta'
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="container mx-auto px-4 sm:px-6 py-12 sm:py-20">
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3 sm:mb-4">How It Works</h2>
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
            Four simple steps to start recovering your lost revenue
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
          <FeatureCard
            icon="1"
            title="Connect"
            description="Link your aggregator accounts or upload CSV exports"
          />
          <FeatureCard
            icon="2"
            title="Detect"
            description="AI identifies suspicious refund patterns automatically"
          />
          <FeatureCard
            icon="3"
            title="Dispute"
            description="Review AI-generated disputes and submit with one click"
          />
          <FeatureCard
            icon="4"
            title="Recover"
            description="Track wins and recovered revenue in real-time"
          />
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-slate-900 py-12 sm:py-20">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4">Why RecoverRefunds?</h2>
            <p className="text-base sm:text-lg text-slate-400 max-w-2xl mx-auto">
              Built by restaurant operators who understand the pain of fraudulent refunds
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <BenefitCard
              icon={ShieldCheckIcon}
              title="AI-Powered Detection"
              description="Our algorithms identify suspicious patterns that humans miss, flagging potential fraud before it costs you."
            />
            <BenefitCard
              icon={BoltIcon}
              title="One-Click Disputes"
              description="Generate professional, evidence-based dispute letters instantly. Submit to any aggregator with a single click."
            />
            <BenefitCard
              icon={ChartBarIcon}
              title="Track Everything"
              description="Real-time analytics on win rates, recovered amounts, and store performance. Know exactly where your money goes."
            />
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="container mx-auto px-4 sm:px-6 py-12 sm:py-20">
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3 sm:mb-4">Simple, Transparent Pricing</h2>
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
            Pay only for results. We take <span className="font-semibold text-blue-600">25-30%</span> of recovered refunds - that's it.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 max-w-5xl mx-auto">
          <PricingCard
            name="Starter"
            price="$0"
            fee="30%"
            stores="5"
            features={['Basic dashboard', 'CSV upload', 'Manual review', 'Email support']}
          />
          <PricingCard
            name="Pro"
            price="$99"
            fee="25%"
            stores="50"
            features={['Full analytics', 'Bulk actions', 'API access', 'Priority support', 'Custom thresholds']}
            highlighted
          />
          <PricingCard
            name="Enterprise"
            price="Custom"
            fee="20%"
            stores="Unlimited"
            features={['SSO integration', 'Custom integrations', 'Dedicated CSM', 'SLA guarantee', 'Custom branding']}
          />
        </div>

        {/* Pricing FAQ */}
        <div className="mt-12 sm:mt-16 max-w-3xl mx-auto">
          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center">
                <CurrencyDollarIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">How our success fee works</h3>
                <p className="text-slate-600 text-sm sm:text-base">
                  You only pay when we help you recover money. Free tier: 30%, Pro: 25%, Enterprise: 20%. Example: On Pro, if we win a $100 refund, you get $75 back.
                  <span className="font-medium"> No win, no fee.</span> Your monthly subscription (if any) covers platform access, analytics, and support.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container mx-auto px-4 sm:px-6 py-12 sm:py-20">
        <div className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl sm:rounded-3xl p-8 sm:p-12 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4">Ready to Recover Your Revenue?</h2>
          <p className="text-base sm:text-lg text-blue-100 mb-6 sm:mb-8 max-w-2xl mx-auto">
            Join 500+ restaurants already using RecoverRefunds to fight fraudulent refunds.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-blue-600 rounded-xl text-lg font-medium hover:bg-blue-50 shadow-lg transition-all"
          >
            Get Started Free
            <ArrowRightIcon className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-slate-50">
        <div className="container mx-auto px-6 py-12">
          {/* Trust Badges */}
          <div className="flex flex-wrap items-center justify-center gap-6 mb-8 pb-8 border-b border-slate-200">
            <div className="flex items-center gap-2 text-slate-500">
              <ShieldCheckIcon className="w-5 h-5" />
              <span className="text-sm font-medium">256-bit SSL</span>
            </div>
            <div className="flex items-center gap-2 text-slate-500">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span className="text-sm font-medium">Read-Only Access</span>
            </div>
            <div className="flex items-center gap-2 text-slate-500">
              <CurrencyDollarIcon className="w-5 h-5" />
              <span className="text-sm font-medium">No Upfront Cost</span>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600">
                <span className="text-sm font-bold text-white">R</span>
              </div>
              <span className="text-slate-500 text-sm">2025 RecoverRefunds. All rights reserved.</span>
            </div>
            <div className="flex gap-6 text-sm text-slate-500">
              <a href="#" className="hover:text-slate-900 transition-colors">Terms</a>
              <a href="#" className="hover:text-slate-900 transition-colors">Privacy</a>
              <a href="#" className="hover:text-slate-900 transition-colors">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function StatCard({ value, label }: { value: string; label: string }) {
  return (
    <div className="text-center lg:text-left">
      <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900">{value}</div>
      <div className="text-xs sm:text-sm text-slate-500">{label}</div>
    </div>
  );
}

function FeatureCard({ icon, title, description }: { icon: string; title: string; description: string }) {
  return (
    <div className="text-center p-4 sm:p-6 bg-white rounded-xl sm:rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
      <div className="w-10 h-10 sm:w-14 sm:h-14 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg sm:rounded-xl flex items-center justify-center text-lg sm:text-xl font-bold mx-auto mb-3 sm:mb-4 shadow-lg shadow-blue-500/30">
        {icon}
      </div>
      <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-1 sm:mb-2">{title}</h3>
      <p className="text-sm sm:text-base text-slate-600">{description}</p>
    </div>
  );
}

function BenefitCard({ icon: Icon, title, description }: { icon: React.ComponentType<{ className?: string }>; title: string; description: string }) {
  return (
    <div className="p-6 bg-slate-800/50 rounded-2xl border border-slate-700">
      <div className="w-12 h-12 bg-blue-500/20 text-blue-400 rounded-xl flex items-center justify-center mb-4">
        <Icon className="w-6 h-6" />
      </div>
      <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
      <p className="text-slate-400">{description}</p>
    </div>
  );
}

function PricingCard({
  name,
  price,
  fee,
  stores,
  features,
  highlighted = false,
}: {
  name: string;
  price: string;
  fee: string;
  stores: string;
  features: string[];
  highlighted?: boolean;
}) {
  return (
    <div className={`relative p-8 bg-white rounded-2xl border shadow-sm ${highlighted ? 'border-blue-500 shadow-lg shadow-blue-500/10' : 'border-slate-200'}`}>
      {highlighted && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-blue-600 to-blue-500 text-white text-xs font-medium rounded-full shadow-lg">
            Most Popular
          </span>
        </div>
      )}
      <h3 className="text-xl font-bold text-slate-900">{name}</h3>
      <div className="mt-4">
        <span className="text-4xl font-bold text-slate-900">{price}</span>
        <span className="text-slate-500">/mo</span>
      </div>
      <div className="text-sm text-slate-500 mt-2">+ {fee} success fee</div>
      <div className="text-sm text-slate-500">Up to {stores} stores</div>
      <ul className="mt-6 space-y-3">
        {features.map((f) => (
          <li key={f} className="flex items-center gap-3 text-sm text-slate-700">
            <div className="flex items-center justify-center w-5 h-5 bg-emerald-50 text-emerald-600 rounded-full">
              <CheckIcon className="w-3 h-3" />
            </div>
            {f}
          </li>
        ))}
      </ul>
      <Link
        to="/login"
        className={`w-full mt-8 py-3 px-4 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2 ${
          highlighted
            ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white hover:from-blue-700 hover:to-blue-600 shadow-lg shadow-blue-500/30'
            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
        }`}
      >
        Get Started
        <ArrowRightIcon className="w-4 h-4" />
      </Link>
    </div>
  );
}
